import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  LayoutDashboard, FileText, Home as HomeIcon, Info, Mail, Layers,
  Share2, Receipt, ListChecks, Image as ImageIcon, HelpCircle, Search,
  Palette, Settings2, DatabaseBackup, User, LogOut, ChevronDown, Bell, Sparkles,
  Menu, X, Activity, DollarSign, BarChart3,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/pages/admin/api";

import Overview from "@/pages/admin/sections/Overview";
import HomepageEditor from "@/pages/admin/sections/HomepageEditor";
import AboutEditor from "@/pages/admin/sections/AboutEditor";
import ContactEditor from "@/pages/admin/sections/ContactEditor";
import FooterEditor from "@/pages/admin/sections/FooterEditor";
import SocialMedia from "@/pages/admin/sections/SocialMedia";
import InvoiceSettings from "@/pages/admin/sections/InvoiceSettings";
import MediaManager from "@/pages/admin/sections/MediaManager";
import ContactMessages from "@/pages/admin/sections/ContactMessages";
import FaqManager from "@/pages/admin/sections/FaqManager";
import SeoSettings from "@/pages/admin/sections/SeoSettings";
import AdsenseSettings from "@/pages/admin/sections/AdsenseSettings";
import AnalyticsSettings from "@/pages/admin/sections/AnalyticsSettings";
import ThemeSettings from "@/pages/admin/sections/ThemeSettings";
import GeneralSettings from "@/pages/admin/sections/GeneralSettings";
import BackupRestore from "@/pages/admin/sections/BackupRestore";
import Profile from "@/pages/admin/sections/Profile";
import ActivityLogs from "@/pages/admin/sections/ActivityLogs";

const MENU = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, Component: Overview },
  { group: "Website Content" },
  { key: "homepage", label: "Homepage", icon: HomeIcon, Component: HomepageEditor },
  { key: "about", label: "About Page", icon: Info, Component: AboutEditor },
  { key: "contact-page", label: "Contact Page", icon: Mail, Component: ContactEditor },
  { key: "footer", label: "Footer", icon: Layers, Component: FooterEditor },
  { group: "Invoice Management" },
  { key: "invoice-settings", label: "Invoice Settings", icon: Receipt, Component: InvoiceSettings },
  { group: "Site Management" },
  { key: "messages", label: "Contact Messages", icon: Mail, Component: ContactMessages },
  { key: "faq", label: "FAQ Management", icon: HelpCircle, Component: FaqManager },
  { key: "media", label: "Media Manager", icon: ImageIcon, Component: MediaManager },
  { key: "social", label: "Social Media", icon: Share2, Component: SocialMedia },
  { group: "Configuration" },
  { key: "seo", label: "SEO & AI Search", icon: Search, Component: SeoSettings },
  { key: "adsense", label: "Google AdSense", icon: DollarSign, Component: AdsenseSettings },
  { key: "analytics", label: "Analytics", icon: BarChart3, Component: AnalyticsSettings },
  { key: "theme", label: "Theme Settings", icon: Palette, Component: ThemeSettings },
  { key: "general", label: "General Settings", icon: Settings2, Component: GeneralSettings },
  { key: "backup", label: "Backup & Restore", icon: DatabaseBackup, Component: BackupRestore },
  { group: "Account" },
  { key: "activity", label: "Activity Logs", icon: Activity, Component: ActivityLogs },
  { key: "profile", label: "Profile", icon: User, Component: Profile },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeKey = searchParams.get("s") || "dashboard";
  const [me, setMe] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => navigate("/admin/login"));
  }, [navigate]);

  const active = MENU.find((m) => m.key === activeKey) || MENU[0];
  const ActiveComponent = active.Component || Overview;

  const setActive = (key) => {
    setSearchParams({ s: key });
    setSidebarOpen(false);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("si-admin-token");
    toast.success("Signed out");
    navigate("/admin/login");
  };

  if (!me) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading dashboard…</div>;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex">
      {/* Sidebar */}
      <aside className={`fixed md:static inset-y-0 left-0 z-40 w-72 shrink-0 flex flex-col bg-white dark:bg-slate-900 border-r border-border transform transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}>
        <div className="h-16 flex items-center gap-2 px-5 border-b border-border">
          <span className="w-9 h-9 rounded-xl bg-blue-600 text-white grid place-items-center"><FileText size={16} /></span>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-sm leading-tight truncate">Simple Invoice</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Admin CMS</div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="md:hidden p-1"><X size={16} /></button>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {MENU.map((m, i) => m.group ? (
            <div key={`g-${i}`} className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-widest text-muted-foreground">{m.group}</div>
          ) : (
            <button
              key={m.key}
              onClick={() => setActive(m.key)}
              data-testid={`nav-${m.key}`}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                activeKey === m.key
                  ? "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 font-medium"
                  : "hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300"
              }`}
            >
              <m.icon size={15} /> {m.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-9 h-9 rounded-full bg-blue-600 text-white grid place-items-center font-heading font-bold text-sm">
              {(me.name || me.email || "A").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{me.name || "Administrator"}</div>
              <div className="text-[10px] text-muted-foreground truncate">{me.email}</div>
            </div>
          </div>
          <Button data-testid="admin-logout" variant="outline" onClick={logout} className="w-full mt-2 rounded-xl">
            <LogOut size={14} className="mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {sidebarOpen && <div onClick={() => setSidebarOpen(false)} className="fixed inset-0 bg-black/40 z-30 md:hidden" />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 flex items-center justify-between px-5 md:px-8 border-b border-border bg-white dark:bg-slate-900 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="md:hidden p-1"><Menu size={18} /></button>
            <div>
              <div className="font-heading font-bold text-base">{active.label || "Dashboard"}</div>
              <div className="text-xs text-muted-foreground">Welcome back, {me.name || "Admin"}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="header-notification" className="w-9 h-9 rounded-lg grid place-items-center hover:bg-accent" aria-label="Notifications"><Bell size={16} /></button>
            <button onClick={() => setActive("profile")} data-testid="header-profile" className="hidden sm:flex items-center gap-2 rounded-lg hover:bg-accent px-2 py-1.5">
              <div className="w-7 h-7 rounded-full bg-blue-600 text-white grid place-items-center text-xs font-bold">
                {(me.name || me.email || "A").charAt(0).toUpperCase()}
              </div>
              <span className="text-sm font-medium">{me.name?.split(" ")[0] || "Admin"}</span>
              <ChevronDown size={14} />
            </button>
          </div>
        </header>

        <main className="flex-1 p-5 md:p-8">
          <ActiveComponent onNavigate={setActive} me={me} />
        </main>
      </div>
    </div>
  );
}

// Shared helpers exported for sections
export { MENU };
